import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { handleApiError, resolveErrorMessage } from '../../api/errorMapping';
// El branding del backend guarda los colores en HSL space-separated (ej. "217 89% 51%"); los pickers
// trabajan en hex. Convertimos en los bordes (load → hex, submit → hsl) con fallback seguro.
import { safeHexToHsl, safeHslToHex } from '../../lib/colorHelpers';
import type { TenantBrandingLocal } from '../../api/types/TenantLocal';
import { ErrorState, HelpTooltip } from '@fiado/react-kit';
import { ContentLoading } from '../shared/ContentLoading';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { BrandingPreview } from './BrandingPreview';

interface FormValues {
    primaryHex: string;
    secondaryHex: string;
    accentHex: string;
    logoUrl: string;
    faviconUrl: string;
    logoWidth: number;
    loginUrl: string;
}

// Defaults = la marca Surekeep (hex equivalente de los --brand-* del :root de index.css:
// primary 217 89% 51% → #1368f1, secondary 210 4% 51% → #7d8287, accent 136 53% 43% → #34a853).
// Antes eran los colores de Google (#1a73e8 / #5f6368), residuo de un seed de ejemplo.
const DEFAULT_FORM: FormValues = {
    primaryHex: '#1368f1',
    secondaryHex: '#7d8287',
    accentHex: '#34a853',
    logoUrl: '',
    faviconUrl: '',
    logoWidth: 100,
    loginUrl: '',
};

export interface BrandingEditorFormProps {
    /** Tenant cuyo branding se edita. */
    tenantId: string;
    /** Carga del branding del tenant. Inyectado por el wrapper (tenant: tenantApi.getBranding; platform: platformApi.getTenantBranding). */
    getBranding: (tenantId: string) => Promise<TenantBrandingLocal>;
    /** Guardado del branding del tenant. Inyectado por el wrapper (tenant: tenantApi.updateBranding; platform: platformApi.updateTenantBranding). */
    updateBranding: (tenantId: string, body: TenantBrandingLocal) => Promise<TenantBrandingLocal>;
    /**
     * Prefijo de la llave de cache de TanStack Query. El `tenantId` se appendea aquí adentro, así cada
     * wrapper queda en su propio namespace sin pisarse (tenant: ['branding']; platform: ['platform',
     * 'branding'] → llaves finales ['branding', tid] / ['platform', 'branding', tid]).
     */
    queryKeyPrefix: readonly string[];
    /**
     * Callback opcional al guardar con éxito. El tenant lo usa para empujar el branding nuevo al
     * brandingStore global (aplicar al instante a la app del usuario). El platform NO lo pasa: edita
     * el branding de OTRO tenant / del platform, no el que el usuario tiene aplicado.
     */
    onSaved?: (branding: TenantBrandingLocal) => void;
    /** Etiqueta del sidebar del preview cuando no hay logo (ej. "Mi organización" / "Plataforma"). */
    sidebarTitle?: string;
}

/**
 * Form + preview de branding COMPARTIDO entre el editor del tenant propio (`BrandingEditor`) y el del
 * módulo Platform (`PlatformBrandingEditor`). Ambos son wrappers delgados que sólo arman las props:
 * a quién editan (`tenantId`), con qué funciones de API (`getBranding`/`updateBranding`), bajo qué
 * `queryKey`, y un `onSaved` opcional (el tenant aplica al store global; el platform no).
 *
 * Como el form + preview se renderizan SIEMPRE desde aquí, la UI es pixel-idéntica en tenant y platform
 * → cero riesgo de divergencia visual.
 *
 * Colores en HSL space-separated en el wire ↔ hex en los pickers: convertimos en los bordes
 * (load → hex, submit → hsl). El form se inicializa una sola vez (refetches posteriores no lo resetean).
 */
export function BrandingEditorForm({
    tenantId,
    getBranding,
    updateBranding,
    queryKeyPrefix,
    onSaved,
    sidebarTitle,
}: BrandingEditorFormProps) {
    const qc = useQueryClient();
    const queryKey = [...queryKeyPrefix, tenantId];
    // `getBranding` está acoplada 1:1 al `queryKeyPrefix` que inyecta el wrapper (no cambia para un
    // prefijo dado), así que NO va en la llave: una fn no es una key de cache serializable.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey,
        queryFn: () => getBranding(tenantId),
        enabled: !!tenantId,
    });

    const [form, setForm] = useState<FormValues>(DEFAULT_FORM);
    const [saved, setSaved] = useState<FormValues>(DEFAULT_FORM);
    const initializedRef = useRef(false);

    // Inicializar SOLO la primera vez que llegan datos — refetches posteriores no resetean el form.
    useEffect(() => {
        if (!data || initializedRef.current) return;
        const initial: FormValues = {
            primaryHex: safeHslToHex(data.primaryColor, DEFAULT_FORM.primaryHex),
            secondaryHex: safeHslToHex(data.secondaryColor, DEFAULT_FORM.secondaryHex),
            accentHex: safeHslToHex(data.accentColor, DEFAULT_FORM.accentHex),
            logoUrl: data.logoUrl,
            faviconUrl: data.faviconUrl ?? '',
            logoWidth: data.logoWidth ?? DEFAULT_FORM.logoWidth,
            loginUrl: data.loginUrl ?? '',
        };
        setForm(initial);
        setSaved(initial);
        initializedRef.current = true;
    }, [data]);

    const isDirty =
        form.primaryHex !== saved.primaryHex ||
        form.secondaryHex !== saved.secondaryHex ||
        form.accentHex !== saved.accentHex ||
        form.logoUrl !== saved.logoUrl ||
        form.faviconUrl !== saved.faviconUrl ||
        form.logoWidth !== saved.logoWidth ||
        form.loginUrl !== saved.loginUrl;

    // Construye el body en HSL (wire) desde el form en hex. Reusado por el PUT y por onSaved (store),
    // así ambos empujan EXACTAMENTE los mismos valores.
    const buildBody = (): TenantBrandingLocal => ({
        primaryColor: safeHexToHsl(form.primaryHex),
        secondaryColor: safeHexToHsl(form.secondaryHex),
        accentColor: safeHexToHsl(form.accentHex),
        logoUrl: form.logoUrl || (data?.logoUrl ?? '/default-logo.svg'),
        faviconUrl: form.faviconUrl || undefined,
        logoWidth: form.logoWidth,
        loginUrl: form.loginUrl.trim() || undefined,
    });

    const mut = useMutation({
        mutationFn: () => updateBranding(tenantId, buildBody()),
        onSuccess: async () => {
            toast.success('Marca actualizada');
            setSaved(form);
            // El wrapper que aplica al store (tenant) recibe aquí el branding nuevo para empujarlo al
            // brandingStore global sin esperar el round-trip; el platform no pasa onSaved.
            onSaved?.(buildBody());
            await qc.invalidateQueries({ queryKey });
        },
        onError: handleApiError,
    });

    if (isLoading) return <ContentLoading />;
    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar la marca"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    return (
        <div className="space-y-4">
            {/* Colores ocultos por ahora: branding = solo logo/favicon. Los colores existentes
                se conservan (el form los sigue enviando desde el estado precargado). */}

            {/* TD-WEBAPP-009: branding con input texto para logo/favicon URL (no S3 upload). Ver TECH_DEBT.md. */}
            <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="logo">Logo principal (URL)</Label>
                    <Input
                        id="logo"
                        type="url"
                        placeholder="https://tu-dominio.com/logo.png"
                        value={form.logoUrl}
                        onChange={(e) => {
                            setForm((f) => ({ ...f, logoUrl: e.target.value }));
                        }}
                    />
                    <p className="text-xs text-muted-foreground">
                        Se muestra en el menú lateral. URL pública (https://…) o ruta local
                        (/logo.svg).
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="favicon">Favicon (ícono de pestaña) (URL)</Label>
                    <Input
                        id="favicon"
                        type="url"
                        placeholder="https://tu-dominio.com/favicon.ico"
                        value={form.faviconUrl}
                        onChange={(e) => {
                            setForm((f) => ({ ...f, faviconUrl: e.target.value }));
                        }}
                    />
                    <p className="text-xs text-muted-foreground">
                        Ícono chico de la pestaña del navegador (32×32 px recomendado). Mira la
                        vista previa en el header de abajo.
                    </p>
                </div>
            </div>

            <div className="space-y-2">
                <div className="flex items-center gap-1">
                    <Label htmlFor="loginUrl">URL de acceso a la plataforma</Label>
                    <HelpTooltip content="URL a la que apunta el botón 'Acceder a la Plataforma' del correo de invitación que reciben los usuarios nuevos de esta organización. Si se deja vacío, se usa la URL por defecto del ambiente." />
                </div>
                <Input
                    id="loginUrl"
                    type="url"
                    placeholder="https://tu-dominio.com/login"
                    value={form.loginUrl}
                    onChange={(e) => {
                        setForm((f) => ({ ...f, loginUrl: e.target.value }));
                    }}
                />
                <p className="text-xs text-muted-foreground">
                    Link del botón del correo de invitación. Vacío ⇒ URL por defecto del ambiente.
                </p>
            </div>

            <div className="space-y-2">
                <div className="flex items-center gap-1">
                    <Label htmlFor="logoWidth">Ancho del logo: {form.logoWidth}%</Label>
                    <HelpTooltip content="Ancho del logo en la barra lateral. Ajusta según tu logo para que se vea bien." />
                </div>
                <input
                    id="logoWidth"
                    type="range"
                    min={15}
                    max={100}
                    step={1}
                    className="w-full accent-brand-accent"
                    value={form.logoWidth}
                    onChange={(e) => {
                        setForm((f) => ({ ...f, logoWidth: Number(e.target.value) }));
                    }}
                />
                <p className="text-xs text-muted-foreground">
                    Ajusta el tamaño del logo en el menú lateral (15–100%). Se mantiene
                    proporcional.
                </p>
            </div>

            <BrandingPreview
                primaryHex={form.primaryHex}
                secondaryHex={form.secondaryHex}
                accentHex={form.accentHex}
                logoUrl={form.logoUrl}
                logoWidth={form.logoWidth}
                faviconUrl={form.faviconUrl || undefined}
                sidebarTitle={sidebarTitle}
            />

            {isDirty && (
                <Button
                    onClick={() => {
                        mut.mutate();
                    }}
                    disabled={mut.isPending}
                >
                    {mut.isPending ? 'Guardando…' : 'Guardar cambios'}
                </Button>
            )}
        </div>
    );
}
